import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Divider, Link, FlexBox } from '@vp/swan';
import { ReplaceTokens, useTranslations } from '@vp/om-translations';
import { OrderSummary, type PriceSubtotal } from '@vp/component-library';
import PropTypes from 'prop-types';
import useSiteData from '../../hooks/useSiteData';
import IfDebug from '../debugTooling/IfDebug';
import { DebugTaxInfo } from '../debugTooling/DebugTaxInfo';
import { DiscountModal } from './DiscountModal';
import { OrderPriceSummaryProps } from '../../types/orderPriceSummary.types';
import { pricingSelectors } from '../../state/pricing';
import { discountHelper } from '../../util/discountHelper';
import type { RootState } from '../../types';

const DeliveryLink = ({ children }: { children: any }) => {
  const {
    externalAppNavigation: { delivery },
  } = useSiteData();
  return (
    <Link href={delivery} target="_blank">
      {children}
    </Link>
  );
};
DeliveryLink.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};

export const OrderPriceSummary = ({
  productSubtotalLabel,
  orderTotalLabel,
  fractionDigits,
  currency,
  isLoaded,
  unit,
  siteLocale,
  shippingPricingDisplayType = 'none',
  shippingIsValid,
  shippingMethodDisplayValue,
  productPrice,
  shippingPrice,
  orderPrice,
  discountSources,
  pricingData,
}: OrderPriceSummaryProps) => {
  const [cartMessages] = useTranslations(['cartMessages']);
  const [itemDiscountModalOpen, setItemDiscountModalOpen] =
    useState<boolean>(false);
  const [shippingDiscountModalOpen, setShippingDiscountModalOpen] =
    useState<boolean>(false);

  const shippingPricing = useSelector((state: RootState) =>
    pricingSelectors.selectShippingPricing(state.pricing)
  );

  const {
    itemDiscount,
    shippingDiscount,
    itemDiscounts: iDiscounts,
    shippingDiscounts: sDiscounts,
  } = discountHelper(pricingData, shippingPricing.pricingData, discountSources);

  const subtotals: PriceSubtotal[] = [];
  if (productPrice) {
    const itemSavedCents =
      productPrice.taxAwareListCents - productPrice.taxAwareDiscountedCents;
    const subtotalDiscounts = itemDiscount
      ? [
          {
            centAmount: itemSavedCents,
            label: cartMessages.savings,
            details: {
              type: itemDiscount.type,
              code: itemDiscount.couponCode,
            },
            unit,
            onClick: () => setItemDiscountModalOpen(true),
          },
        ]
      : undefined;

    subtotals.push({
      listCents: productPrice.taxAwareListCents,
      discountedCents: productPrice.taxAwareDiscountedCents,
      rowLabel: productSubtotalLabel ?? cartMessages.itemSubtotal,
      includedTaxes: productPrice.includedTaxes,
      excludedTaxes: productPrice.excludedTaxes,
      unit,
      discounts: subtotalDiscounts,
    });
  }

  const shippingLabelKey = shippingIsValid
    ? 'shippingSubtotal'
    : 'shippingCalculatedInCheckout';

  // if the shipping pricing display type is not none, we will display a shipping value
  const displayShippingValue = shippingPricingDisplayType !== 'none';
  let displayShippingIsNotIncluded = false;

  if (isLoaded) {
    // if we are told to display shipping prices AND that data is available...
    if (
      (shippingPricingDisplayType !== 'price' ||
        !shippingPrice ||
        !shippingMethodDisplayValue) &&
      displayShippingValue
    ) {
      // if we are told to display either
      // * shipping not included,
      // * a price, but we don't have a price available
      // display not included
      displayShippingIsNotIncluded = true;
    }
  }

  const shippingDiscounts = shippingDiscount
    ? [
        {
          centAmount: shippingPrice
            ? shippingPrice.taxAwareListCents -
              shippingPrice.taxAwareDiscountedCents
            : 0,
          label: cartMessages.savings,
          details: {
            type: shippingDiscount.type,
            code: shippingDiscount.couponCode,
          },
          onClick: () => setShippingDiscountModalOpen(true),
        },
      ]
    : undefined;

  if (displayShippingIsNotIncluded) {
    // formattedShippingPrice
    subtotals.push({
      listCents: 0,
      discountedCents: 0,
      rowLabel: (
        <ReplaceTokens
          tokenizedString={cartMessages.shippingCalculatedInCheckout}
          substitutions={{
            DeliveryLink,
          }}
        />
      ),
      includedTaxes: [],
      excludedTaxes: [],
      unit,
      zeroOverride: '-',
    });
  } else if (displayShippingValue && shippingPrice) {
    // formattedShippingPrice
    subtotals.push({
      listCents: shippingPrice.taxAwareListCents,
      discountedCents: shippingPrice.taxAwareDiscountedCents,
      rowLabel: (
        <ReplaceTokens
          tokenizedString={cartMessages[shippingLabelKey]}
          substitutions={{
            shippingSpeed: shippingMethodDisplayValue,
          }}
        />
      ),
      includedTaxes: shippingPrice.includedTaxes,
      excludedTaxes: shippingPrice.excludedTaxes,
      unit,
      discounts: shippingDiscounts,
    });
  }

  // format aggregated order price for display using the component-library
  const formattedOrderPrice = {
    listCents: orderPrice.taxAwareListCents,
    discountedCents: orderPrice.taxAwareDiscountedCents,
    rowLabel: orderTotalLabel ?? cartMessages.total,
    includedTaxes: orderPrice.includedTaxes,
    excludedTaxes: orderPrice.excludedTaxes,
    unit,
  };

  let selectedDiscountId: string = '';
  if (itemDiscount) {
    selectedDiscountId = itemDiscount.promotionId;
  } else if (shippingDiscount) {
    selectedDiscountId = shippingDiscount.promotionId;
  }

  let itemDiscountModal = null;
  if (
    itemDiscount?.id &&
    itemDiscount.details &&
    itemDiscount.details[siteLocale]
  ) {
    itemDiscountModal = (
      <DiscountModal
        open={itemDiscountModalOpen}
        setIsOpen={setItemDiscountModalOpen}
        discounts={{
          itemDiscounts: iDiscounts || undefined,
          shippingDiscounts: sDiscounts || undefined,
        }}
        selectedDiscountId={selectedDiscountId}
      />
    );
  }

  let shippingDiscountModal = null;
  if (
    shippingDiscount?.id &&
    shippingDiscount.details &&
    shippingDiscount.details[siteLocale]
  ) {
    shippingDiscountModal = (
      <DiscountModal
        open={shippingDiscountModalOpen}
        setIsOpen={setShippingDiscountModalOpen}
        discounts={{
          itemDiscounts: iDiscounts || undefined,
          shippingDiscounts: sDiscounts || undefined,
        }}
        selectedDiscountId={shippingDiscount.promotionId}
      />
    );
  }

  return (
    <div role="table">
      <OrderSummary
        fractionDigits={fractionDigits}
        currencyCode={currency}
        locale={siteLocale}
        order={formattedOrderPrice}
        subtotals={subtotals}
        isLoading={!isLoaded}
        wrappedInTable
      />
      <IfDebug>
        <FlexBox justifyContent="right">
          <DebugTaxInfo
            aggregatedTaxAwarePrice={orderPrice}
            header="Order"
            displaySubtotals
          />
        </FlexBox>
      </IfDebug>
      <Divider marginTop={4} />
      {itemDiscountModal}
      {shippingDiscountModal}
    </div>
  );
};

const aggregatedPrice = PropTypes.shape({
  includedTaxes: PropTypes.array,
  excludedTaxes: PropTypes.array,
  taxAwareDiscountedCents: PropTypes.number,
  taxAwareListCents: PropTypes.number,
});

OrderPriceSummary.propTypes = {
  orderTotalLabel: PropTypes.string,
  productSubtotalLabel: PropTypes.string,
  fractionDigits: PropTypes.number.isRequired,
  currency: PropTypes.string.isRequired,
  unit: PropTypes.string,
  siteLocale: PropTypes.string.isRequired,
  shippingPricingDisplayType: PropTypes.string,
  shippingIsValid: PropTypes.bool,
  shippingMethodDisplayValue: PropTypes.string,
  orderPrice: aggregatedPrice.isRequired,
  productPrice: aggregatedPrice,
  shippingPrice: aggregatedPrice,
  isLoaded: PropTypes.bool,
  discountSources: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  pricingData: PropTypes.object,
};
